import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [consultationForm, setConsultationForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [consultationSubmitted, setConsultationSubmitted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Observe homepage sections to highlight active nav item
  useEffect(() => {
    if (location.pathname !== '/') return;

    const ids = ['hero', 'services', 'calculator', 'projects', 'blog', 'testimonials'];
    let observer;

    const createObserver = () => {
      const elements = ids.map((id) => document.getElementById(id)).filter(Boolean);
      if (elements.length === 0) return null;

      const inst = new IntersectionObserver(
        (entries) => {
          const visibleEntries = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (visibleEntries[0]) {
            setActiveSection(visibleEntries[0].target.id);
          }
        },
        {
          // Offset top so header doesn't steal focus area
          rootMargin: '-72px 0px -50% 0px',
          threshold: [0.1, 0.25, 0.5, 0.75]
        }
      );

      elements.forEach((el) => inst.observe(el));
      return inst;
    };

    observer = createObserver();
    // Re-scan shortly after mount to catch sections that render later
    const retry = window.setTimeout(() => {
      if (observer) observer.disconnect();
      observer = createObserver();
    }, 300);

    return () => {
      if (observer) observer.disconnect();
      window.clearTimeout(retry);
    };
  }, [location.pathname]);

  // Fallback: scroll-based detection to ensure active section updates reliably
  useEffect(() => {
    if (location.pathname !== '/') return;

    const ids = ['hero', 'services', 'calculator', 'projects', 'blog', 'testimonials'];
    const headerOffsetPx = 72;

    const onScroll = () => {
      let closestSectionId = activeSection;
      let smallestDistance = Number.POSITIVE_INFINITY;

      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top - headerOffsetPx);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          closestSectionId = id;
        }
      }

      if (closestSectionId && closestSectionId !== activeSection) {
        setActiveSection(closestSectionId);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once on mount to set initial state
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname, activeSection]);

  // Lock body scroll when mobile menu or modal is open
  useEffect(() => {
    const shouldLock = isMobileMenuOpen || showConsultationModal;
    document.body.style.overflow = shouldLock ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen, showConsultationModal]);

  // Allow closing consultation modal with Escape key
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowConsultationModal(false);
      }
    };
    if (showConsultationModal) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showConsultationModal]);

  // Global event to open consultation modal from anywhere
  useEffect(() => {
    const openHandler = () => setShowConsultationModal(true);
    window.addEventListener('open-consultation-modal', openHandler);
    return () => window.removeEventListener('open-consultation-modal', openHandler);
  }, []);

  const navigationItems = [
    {
      name: 'Home',
      path: '/',
      icon: 'Home',
      section: 'hero'
    },
    {
      name: 'Services',
      path: '/',
      icon: 'Users',
      section: 'services'
    },
    {
      name: 'Calculator',
      path: '/',
      icon: 'Calculator',
      section: 'calculator'
    },
    {
      name: 'Projects',
      path: '/',
      icon: 'FolderOpen',
      section: 'projects'
    },
    {
      name: 'Blog',
      path: '/',
      icon: 'FileText',
      section: 'blog'
    },
    {
      name: 'Testimonials',
      path: '/',
      icon: 'Star',
      section: 'testimonials'
    }
  ];

  const secondaryItems = [
    {
      name: 'About',
      path: '/aboutUs',
      icon: 'Info'
    },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const scrollToSection = (sectionId) => {
    closeMobileMenu();
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 64; // 16 * 4 = 64px (h-16)
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleNavClick = (item) => {
    if (item.section) {
      // If we're not on the homepage, navigate there first
      if (location.pathname !== '/') {
        navigate('/');
        // Wait for navigation to complete, then scroll
        setTimeout(() => {
          scrollToSection(item.section);
        }, 100);
      } else {
        scrollToSection(item.section);
      }
    } else {
      // Regular navigation for external pages
      closeMobileMenu();
    }
  };

  const handleConsultationClick = () => {
    setShowConsultationModal(true);
    closeMobileMenu();
  };

  const handleConsultationModalClose = () => {
    setShowConsultationModal(false);
    setConsultationForm({ name: '', phone: '', email: '', message: '' });
    setConsultationSubmitted(false);
  };

  const handleConsultationFormChange = (e) => {
    const { name, value } = e.target;
    setConsultationForm(prev => ({ ...prev, [name]: value }));
  };

  const handleConsultationSubmit = (e) => {
    e.preventDefault();
    const { name, phone, email, message } = consultationForm;
    const text = [
      `Hi, I'd like to schedule a free consultation.`,
      ``,
      `*Name:* ${name}`,
      `*Phone:* ${phone}`,
      `*Email:* ${email}`,
      message ? `*Project Details:* ${message}` : '',
    ].filter(Boolean).join('\n');
    const whatsappUrl = `https://wa.me/918003989393?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
    setConsultationSubmitted(true);
  };

  return (
    <>
    <header 
      className="fixed top-0 left-0 right-0 z-50 transition-construction bg-transparent"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
        <div className={`max-w-7xl mx-auto flex items-center justify-between h-14 px-3 sm:px-4 border border-border rounded-2xl backdrop-blur bg-white/80 ${isScrolled ? 'construction-shadow' : ''}`}> 
          {/* Mobile Logo */}
          <Link
            to="/"
            onClick={closeMobileMenu}
            className="lg:hidden flex items-center min-w-0"
            aria-label="Go to home"
          >
            <img
              src="/assets/images/logo.png"
              alt="Nirmitee Associates"
              className="h-7 w-auto"
              loading="eager"
            />
          </Link>

          {/* Desktop spacer to balance layout when logo shows in hero */}
          <div className="hidden lg:block w-6" aria-hidden="true"></div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
                item.section ? (
                  <button
                    key={item.name}
                    onClick={() => handleNavClick(item)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-construction ${
                      location.pathname === '/' && activeSection === item.section
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted hover:text-primary'
                    }`}
                  >
                    <Icon name={item.icon} size={16} />
                    <span className="font-medium">{item.name}</span>
                  </button>
                ) : (
              <Link
                    key={item.name}
                to={item.path}
                    onClick={closeMobileMenu}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-construction ${
                  isActivePath(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted hover:text-primary'
                }`}
              >
                <Icon name={item.icon} size={16} />
                    <span className="font-medium">{item.name}</span>
              </Link>
                )
              ))}
              
              {/* Divider */}
              <div className="w-px h-6 bg-border mx-2"></div>
              
                  {secondaryItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-construction ${
                        isActivePath(item.path)
                          ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted hover:text-primary'
                      }`}
                    >
                      <Icon name={item.icon} size={16} />
                  <span className="font-medium">{item.name}</span>
                    </Link>
                  ))}

              {/* Divider */}
              <div className="w-px h-6 bg-border mx-2"></div>

          {/* CTA Button */}
            <Button 
              variant="default" 
              size="sm"
              iconName="Calendar"
              iconPosition="left"
                onClick={handleConsultationClick}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-5"
            >
                Book Free Consultation
            </Button>
            </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-full text-foreground hover:bg-muted transition-construction"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </button>
        </div>

          {/* Mobile Navigation */}
        {isMobileMenuOpen && (
            <div id="mobile-navigation" className="lg:hidden border border-border bg-white rounded-2xl mt-2">
              <nav className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                  item.section ? (
                    <button
                      key={item.name}
                      onClick={() => handleNavClick(item)}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-left transition-construction hover:bg-muted ${
                        location.pathname === '/' && activeSection === item.section ? 'bg-muted' : ''
                      }`}
                    >
                      <Icon name={item.icon} size={18} />
                      <span className="font-medium">{item.name}</span>
                    </button>
                  ) : (
                <Link
                      key={item.name}
                  to={item.path}
                  onClick={closeMobileMenu}
                      className="flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-left transition-construction hover:bg-muted"
                >
                  <Icon name={item.icon} size={18} />
                      <span className="font-medium">{item.name}</span>
                </Link>
                  )
              ))}
              
                {/* Divider */}
                <div className="border-t border-border my-2"></div>
                
                {secondaryItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={closeMobileMenu}
                    className="flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-left transition-construction hover:bg-muted"
                  >
                    <Icon name={item.icon} size={18} />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                ))}

                {/* Mobile CTA Button */}
                <div className="pt-4">
                <Button 
                  variant="default" 
                  fullWidth
                  iconName="Calendar"
                  iconPosition="left"
                    onClick={handleConsultationClick}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
                  >
                    Book Free Consultation
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Spacer to prevent content from hiding behind fixed header */}
      <div className="h-16" aria-hidden="true"></div>

      {/* Consultation Modal */}
      {showConsultationModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleConsultationModalClose}
        >
          <div
            className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Book Free Consultation</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Get professional guidance from our construction experts
                  </p>
                </div>
                <button
                  onClick={handleConsultationModalClose}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* WhatsApp Contact Option - match Get Quote modal theme */}
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <Icon name="MessageCircle" size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-800">Quick Consultation</h3>
                    <p className="text-sm text-green-600">Get instant response via WhatsApp</p>
                  </div>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  fullWidth
                  className="mt-3 bg-green-500 hover:bg-green-600"
                  onClick={() => {
                    const message = `Hi, I need expert consultation for my construction project. Can you please help me with professional guidance?`;
                    const whatsappUrl = `https://wa.me/918003989393?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                >
                  <Icon name="MessageCircle" size={16} className="mr-2" />
                  Chat on WhatsApp
                </Button>
              </div>

              {/* Divider */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card text-muted-foreground">Or schedule a detailed consultation</span>
                </div>
              </div>

              {/* Consultation Form or Success Message */}
              {consultationSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircle" size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Message Sent!</h3>
                  <p className="text-muted-foreground mb-6">Your consultation request has been sent via WhatsApp. We'll get back to you shortly.</p>
                  <Button variant="default" onClick={handleConsultationModalClose} className="bg-primary hover:bg-primary/90">
                    Done
                  </Button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleConsultationSubmit}>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      value={consultationForm.name}
                      onChange={handleConsultationFormChange}
                      placeholder="Full Name"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={consultationForm.phone}
                      onChange={handleConsultationFormChange}
                      placeholder="Phone Number"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    />
                  </div>

                  <input
                    type="email"
                    name="email"
                    value={consultationForm.email}
                    onChange={handleConsultationFormChange}
                    placeholder="Email Address"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    required
                  />

                  <textarea
                    name="message"
                    value={consultationForm.message}
                    onChange={handleConsultationFormChange}
                    placeholder="Tell us about your project and consultation needs..."
                    className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    rows={4}
                  />

                  <div className="flex space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleConsultationModalClose}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="default"
                      className="flex-1 bg-primary hover:bg-primary/90"
                    >
                      Schedule via WhatsApp
                    </Button>
                  </div>
                </form>
              )}
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default Header;